import { Condition, ConditionData } from "../domain/Condition.entity";
import { ConditionTranslationData } from "../domain/ConditionTranslation.entity";
import { ConditionTypeEnum } from "../domain/ConditionType.enum";
import { FlightHotelPaymentType } from "../domain/FlightHotelPaymentType.enum";
import { ConditionDto } from "./Condition.dto";

export class ConditionMapper {
  static toDomain(dto: ConditionDto): Condition {
    const conditionData: ConditionData = {
      id: dto.id,
      propertyTicker: dto.propertyTicker,
      ticker: dto.ticker,
      code: dto.code,
      name: dto.name,
      type: dto.type,
      conditionType: this.mapStringToConditionType(dto.conditionType),
      color: dto.color,
      ordering: dto.ordering,
      chargeFirstNight: dto.chargeFirstNight,
      earlyCharge: dto.earlyCharge,
      minimumCharge: dto.minimumCharge,
      translations: this.mapTranslationsToDomain(dto.translations),
      policies: dto.policies,
      paymentTickers: dto.payments,
      flightHotelPaymentType: this.mapStringToFlightHotelPaymentType(dto.flightHotelPaymentType),
    };

    return Condition.of(conditionData);
  }

  static toDto(condition: Condition): ConditionDto {
    return {
      id: condition.id,
      propertyTicker: condition.propertyTicker,
      ticker: condition.ticker,
      code: condition.code,
      name: condition.name,
      type: condition.type,
      conditionType: condition.conditionType,
      color: condition.color,
      ordering: condition.ordering,
      chargeFirstNight: condition.chargeFirstNight,
      earlyCharge: condition.earlyCharge,
      minimumCharge: condition.minimumCharge,
      translations: this.mapTranslationsToDto(condition.translations),
      policies: condition.policies,
      payments: condition.paymentTickers,
      flightHotelPaymentType: condition.flightHotelPaymentType,
    };
  }

  private static mapStringToConditionType(type: string): ConditionTypeEnum {
    switch (type.toUpperCase()) {
      case "ENGINE":
        return ConditionTypeEnum.ENGINE;
      case "STORE":
        return ConditionTypeEnum.STORE;
      case "FLIGHT_HOTEL":
        return ConditionTypeEnum.FLIGHT_HOTEL;
      default:
        // Default to ENGINE if type is unknown
        return ConditionTypeEnum.ENGINE;
    }
  }

  private static mapStringToFlightHotelPaymentType(
    type: string | undefined
  ): FlightHotelPaymentType | undefined {
    switch (type?.toUpperCase()) {
      case "FLIGHT_ONLY":
        return FlightHotelPaymentType.FLIGHT_ONLY;
      case "ALL_NOW":
        return FlightHotelPaymentType.ALL_NOW;
      case "FLIGHT_NOW_HOTEL_LATER":
        return FlightHotelPaymentType.FLIGHT_NOW_HOTEL_LATER;
      default:
        // Unknown or absent -> undefined. Deliberately NOT coerced to FLIGHT_ONLY (unlike
        // mapStringToConditionType's ENGINE fallback) so a corrupt value never silently becomes
        // a valid one. Only FLIGHT_HOTEL conditions carry a value.
        return undefined;
    }
  }

  private static mapTranslationsToDomain(
    dtoTranslations: ConditionDto["translations"]
  ): ConditionTranslationData[] {
    return dtoTranslations.map(translation => ({
      name: translation.name,
      locale: translation.locale,
      text: translation.text,
      html: translation.html,
      htmlCancellation: translation.htmlCancellation,
      htmlCheckIn: translation.htmlCheckIn,
      htmlCheckOut: translation.htmlCheckOut,
      htmlChildren: translation.htmlChildren,
      htmlPets: translation.htmlPets,
      htmlGroups: translation.htmlGroups,
      htmlExtraInfo: translation.htmlExtraInfo,
    }));
  }

  private static mapTranslationsToDto(translations: any[]): ConditionDto["translations"] {
    return translations.map(translation => ({
      name: translation.name,
      locale: translation.locale,
      text: translation.text,
      html: translation.html,
      htmlCancellation: translation.htmlCancellation,
      htmlCheckIn: translation.htmlCheckIn,
      htmlCheckOut: translation.htmlCheckOut,
      htmlChildren: translation.htmlChildren,
      htmlPets: translation.htmlPets,
      htmlGroups: translation.htmlGroups,
      htmlExtraInfo: translation.htmlExtraInfo,
    }));
  }
}
